import React, { useEffect, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';
import YandexMap from 'src/components/YandexMap';
import { Spinner } from 'src/components/animations/Spinner';
import styled from 'styled-components';
import type { YMap } from '@yandex/ymaps3-types';
import {
  DESKTOP_OFFSET_AFTER_HEADER,
  FULL_MOBILE_OFFSET,
  HEADER_BANNER_OFFSET,
  HEADER_OFFSET_SEARCH,
} from 'src/constants/header';
import type {
  Coordinates,
  ShortHotelInfo,
} from 'src/components/YandexMap/types';
import { loadReactComponent } from 'src/components/YandexMap/loaders/componentLoaders';
import { loadModulePromise } from 'src/components/YandexMap/mapLoader';
import { DEFAULT_EASING_DURATION } from 'src/components/YandexMap/constants';
import type { PropsFromRedux } from '.';
import Clusterer from './components/Clusterer';
import Controls from './components/Controls';
import HotelPopup from './components/HotelPopup';
import { ACTIVE_HOTEL_ZOOM } from './constants';
import useShortPress from './hooks/useShortPress';
import { pushMapExpanded, pushMapOpened } from './analytics';
import { DEFAULT_MAP_POSITION } from '../../store/map/constants';
import ActiveHotelLayer from './components/ActiveHotelLayer';
import { calculateMapCenter } from './utils';

const YMapListener = loadReactComponent('YMapListener', loadModulePromise);

const YandexMapsSearch = (props: PropsFromRedux) => {
  const {
    hotelsList,
    activeHotel,
    mapPosition,
    isMobile,
    getMapDataHandler,
    isExpanded,
    toggleHandler,
    resetPopupDataHandler,
    hotelInfoForPopup,
    isHeaderBannerHidden = false,
  } = props;

  const [fetchingMapInfo, setFetchingMapInfo] = React.useState(true);

  const [location, setLocation] = React.useState(mapPosition?.coordinates);
  const [bounds, setBounds] = React.useState(mapPosition?.bounds);
  const [zoom, setZoom] = React.useState(mapPosition.zoom);

  const readonlyLocation = useRef(mapPosition.coordinates);
  const readonlyZoom = useRef(mapPosition.zoom);

  const points = useMemo(
    () =>
      hotelsList.map((hotel) => {
        const { coordinates, ...otherHotelInfo } = hotel.properties;
        return {
          coordinates: coordinates as [number, number],
          shortHotelInfo: otherHotelInfo as ShortHotelInfo,
        };
      }),
    [hotelsList],
  );

  // После маунта компонента дергаем ручку для получения границ региона
  useEffect(() => {
    getMapDataHandler();
  }, [getMapDataHandler]);

  // После прихода данных по границам региона или центру региона устанавливаем их
  useEffect(() => {
    if (!fetchingMapInfo) return;

    if (!isEqual(mapPosition?.coordinates, DEFAULT_MAP_POSITION)) {
      setLocation(mapPosition.coordinates);
      setZoom(mapPosition.zoom);
      // Учитываем, что карта перемещается не моментально
      setTimeout(() => setFetchingMapInfo(false), DEFAULT_EASING_DURATION);
    }
    if (mapPosition?.bounds) {
      setBounds(mapPosition.bounds);
      // Учитываем, что карта перемещается не моментально
      setTimeout(() => setFetchingMapInfo(false), DEFAULT_EASING_DURATION);
    }
  }, [mapPosition]);

  // Зум на отель при наведении на карточку в поиске
  // При этом убираем bounds, так как при использовании bounds карта не учитывает center
  useEffect(() => {
    if (activeHotel && activeHotel.zoomRequired) {
      // Сбрасываем bounds, потому что с ними не работает зум по клику на активный отель
      if (bounds) {
        setBounds(undefined);
      }
      setLocation(activeHotel.coordinates);
      setZoom(ACTIVE_HOTEL_ZOOM);
    }
  }, [activeHotel, setBounds, bounds]);

  useEffect(() => {
    pushMapOpened();
  }, []);

  // Функция для отслеживания движения юзера по карте
  const onUpdate = React.useCallback((params) => {
    const { mapInAction, location: currentLocation } = params;
    if (!mapInAction) {
      readonlyLocation.current = currentLocation.center;
      readonlyZoom.current = currentLocation.zoom;
    }
  }, []);

  const mapRef = useRef<YMap>(null);

  /**
   * Перемещает позицию карты
   * @param coordinates Координаты центра карты
   * @param zoomDiff Разница в зуме, может быть положительной и отрицательной
   * @param offset Отступ
   */
  const moveToPoint = (
    coordinates: Coordinates,
    zoomDiff: number,
    offset: { x: number; y: number },
  ) => {
    // Сбрасываем bounds, потому что с ними не работает зум по клику на кластер
    if (bounds) {
      setBounds(undefined);
    }

    if (!mapRef.current) return;

    // Яндекс не умеет в оффсеты в версии 3.0, поэтому перевычисляем центр карты с учетом отступа
    const coordinatesWithOffset = calculateMapCenter(
      mapRef.current?.bounds[0],
      mapRef.current?.bounds[1],
      coordinates,
      mapRef.current?.size,
      offset,
    );

    setLocation(coordinatesWithOffset);
    setZoom(readonlyZoom.current + zoomDiff);
  };

  const popupRef = useRef<HTMLDivElement>(null);

  const adjustMapToCurrentPosition = () => {
    setLocation(readonlyLocation.current);
    setZoom(readonlyZoom.current);
  };

  const toggleExpandMap = () => {
    toggleHandler();
    pushMapExpanded(isExpanded);
    adjustMapToCurrentPosition();
  };

  // Закрываем попап, когда кликаем по карте снаружи попапа
  useShortPress(popupRef, () => {
    if (hotelInfoForPopup) {
      resetPopupDataHandler();
      adjustMapToCurrentPosition();
    }
  });

  return (
    <YandexMapSearchContainer
      isHeaderBannerHidden={isHeaderBannerHidden}
      isMobile={isMobile}
    >
      {fetchingMapInfo && (
        <MapLoaderBg>
          <Spinner size={64} />
        </MapLoaderBg>
      )}
      <YandexMap center={location} bounds={bounds} zoom={zoom} mapRef={mapRef}>
        <YMapListener onUpdate={onUpdate} />
        <Controls
          isMobile={isMobile}
          isExpanded={isExpanded}
          toggleHandler={toggleExpandMap}
        />

        <ActiveHotelLayer
          moveToPoint={moveToPoint}
          coordinates={activeHotel?.coordinates}
        />

        <Clusterer
          isMobile={isMobile}
          moveToPoint={moveToPoint}
          activeHotel={activeHotel}
          points={points}
        />
        <HotelPopup
          popupRef={popupRef}
          onClosePopup={adjustMapToCurrentPosition}
        />
      </YandexMap>
    </YandexMapSearchContainer>
  );
};

const MapLoaderBg = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
  background-color: rgb(184, 184, 184);
`;

const countFullDesktopOffset = (isHeaderBannerHidden: boolean) => {
  if (isHeaderBannerHidden)
    return HEADER_OFFSET_SEARCH + DESKTOP_OFFSET_AFTER_HEADER;
  return (
    HEADER_OFFSET_SEARCH + DESKTOP_OFFSET_AFTER_HEADER + HEADER_BANNER_OFFSET
  );
};

const YandexMapSearchContainer = styled.div<{
  isMobile?: boolean;
  isHeaderBannerHidden: boolean;
}>`
  height: calc(
    100vh -
      ${({ isMobile, isHeaderBannerHidden }) =>
        isMobile
          ? FULL_MOBILE_OFFSET
          : countFullDesktopOffset(isHeaderBannerHidden)}px
  );
  top: ${({ isMobile }) =>
    isMobile ? 0 : HEADER_OFFSET_SEARCH + DESKTOP_OFFSET_AFTER_HEADER}px;
  position: ${({ isMobile }) => (isMobile ? 'relative' : 'sticky')} !important;
`;

export default YandexMapsSearch;
